/**
 * Pages an industry blueprint can switch on/off and the words it can rename.
 * Mirrors backend/src/common/industry/crm-pages.ts — keep them in step. The
 * workspace endpoint sends the resolved values; these are the fallbacks used
 * before it answers (and for the General CRM).
 */
export type PageKey =
  | 'dashboard' | 'reports' | 'workflows'
  | 'leads' | 'leads_analytics' | 'leads_ai_analytics' | 'leads_ai_review' | 'web_forms'
  | 'contacts' | 'groups'
  | 'deals' | 'tasks' | 'documents' | 'calendar'
  | 'appointments' | 'appointment_services' | 'bookings'
  | 'catalog' | 'orders' | 'tickets' | 'reservations' | 'staff' | 'stock' | 'projects' | 'retainers'
  | 'properties' | 'viewings' | 'leases' | 'owners';

export interface CrmPageDef { key: PageKey; label: string; group: string; general: boolean }

export const CRM_PAGES: CrmPageDef[] = [
  { key: 'dashboard', label: 'Dashboard', group: 'Overview', general: true },
  { key: 'reports', label: 'Reports', group: 'Overview', general: true },
  { key: 'workflows', label: 'Workflows', group: 'Overview', general: true },
  { key: 'leads', label: 'Leads inbox', group: 'Leads', general: true },
  { key: 'leads_analytics', label: 'Lead analytics', group: 'Leads', general: true },
  { key: 'leads_ai_analytics', label: 'AI analytics', group: 'Leads', general: true },
  { key: 'leads_ai_review', label: 'AI review', group: 'Leads', general: true },
  { key: 'web_forms', label: 'Web forms', group: 'Leads', general: true },
  { key: 'contacts', label: 'Contacts', group: 'Audience', general: true },
  { key: 'groups', label: 'Groups', group: 'Audience', general: true },
  { key: 'deals', label: 'Deals', group: 'Sales', general: true },
  { key: 'tasks', label: 'Tasks', group: 'Sales', general: true },
  { key: 'documents', label: 'Documents', group: 'Sales', general: true },
  { key: 'calendar', label: 'Calendar', group: 'Sales', general: true },
  { key: 'appointments', label: 'Appointment calendar', group: 'Appointments', general: true },
  { key: 'appointment_services', label: 'Appointment services', group: 'Appointments', general: true },
  { key: 'bookings', label: 'Bookings', group: 'Appointments', general: true },
  { key: 'catalog', label: 'Catalog', group: 'Operations', general: false },
  { key: 'orders', label: 'Orders', group: 'Operations', general: false },
  { key: 'tickets', label: 'Tickets', group: 'Operations', general: false },
  { key: 'reservations', label: 'Tables', group: 'Operations', general: false },
  { key: 'staff', label: 'Staff & shifts', group: 'Operations', general: false },
  { key: 'stock', label: 'Stock', group: 'Operations', general: false },
  { key: 'projects', label: 'Projects & time', group: 'Operations', general: false },
  { key: 'retainers', label: 'Retainers', group: 'Operations', general: false },
  { key: 'properties', label: 'Properties & listings', group: 'Operations', general: false },
  { key: 'viewings', label: 'Viewings', group: 'Operations', general: false },
  { key: 'leases', label: 'Leases', group: 'Operations', general: false },
  { key: 'owners', label: 'Owners & tenants', group: 'Operations', general: false },
];

export const GENERAL_PAGES = Object.fromEntries(CRM_PAGES.map((p) => [p.key, p.general])) as Record<PageKey, boolean>;

export type LabelKey =
  | 'leads' | 'contacts' | 'deals' | 'appointments' | 'services' | 'bookings'
  | 'catalog' | 'orders' | 'tickets' | 'reservations' | 'staff' | 'stock' | 'projects' | 'retainers'
  | 'properties' | 'viewings' | 'leases' | 'operations';

export interface CrmLabel { singular: string; plural: string }

export const LABEL_DEFAULTS: Record<LabelKey, CrmLabel> = {
  leads: { singular: 'Lead', plural: 'Leads' },
  contacts: { singular: 'Contact', plural: 'Contacts' },
  deals: { singular: 'Deal', plural: 'Deals' },
  appointments: { singular: 'Appointment', plural: 'Appointments' },
  services: { singular: 'Service', plural: 'Services' },
  bookings: { singular: 'Booking', plural: 'Bookings' },
  catalog: { singular: 'Item', plural: 'Catalog' },
  orders: { singular: 'Order', plural: 'Orders' },
  tickets: { singular: 'Ticket', plural: 'Tickets' },
  reservations: { singular: 'Table', plural: 'Tables' },
  staff: { singular: 'Staff member', plural: 'Staff & shifts' },
  stock: { singular: 'Stock item', plural: 'Stock' },
  projects: { singular: 'Project', plural: 'Projects' },
  retainers: { singular: 'Retainer', plural: 'Retainers' },
  properties: { singular: 'Property', plural: 'Properties' },
  viewings: { singular: 'Viewing', plural: 'Viewings' },
  leases: { singular: 'Lease', plural: 'Leases' },
  operations: { singular: 'Operations', plural: 'Operations' },
};

/**
 * Route prefix → page, most specific first. The layout guard uses it to stop a
 * hidden page from opening via a bookmark or a link.
 */
export const ROUTE_PAGES: Array<[string, PageKey]> = [
  ['/appointments/services', 'appointment_services'],
  ['/appointments/bookings', 'bookings'],
  ['/appointments', 'appointments'],
  ['/leads/analytics', 'leads_analytics'],
  ['/leads/ai-analytics', 'leads_ai_analytics'],
  ['/leads/ai-review', 'leads_ai_review'],
  ['/leads/web-forms', 'web_forms'],
  // Bot flows also sit under WhatsApp → Automation; hiding the leads inbox must not hide them.
  ['/leads/bot-flows', 'dashboard'],
  ['/leads', 'leads'],
  ['/contacts', 'contacts'],
  ['/groups', 'groups'],
  ['/deals', 'deals'],
  ['/tasks', 'tasks'],
  ['/documents', 'documents'],
  ['/calendar', 'calendar'],
  ['/reports', 'reports'],
  ['/workflows', 'workflows'],
  ['/catalog', 'catalog'],
  ['/orders', 'orders'],
  ['/tickets', 'tickets'],
  ['/tables', 'reservations'],
  ['/staff', 'staff'],
  ['/stock', 'stock'],
  ['/projects', 'projects'],
  ['/retainers', 'retainers'],
  ['/properties', 'properties'],
  ['/viewings', 'viewings'],
  ['/leases', 'leases'],
  ['/owners', 'owners'],
];

export function pageForPath(pathname: string): PageKey | null {
  for (const [prefix, page] of ROUTE_PAGES) {
    if (pathname === prefix || pathname.startsWith(`${prefix}/`)) return page;
  }
  return null;
}
